/**
 * ⑤ Confirmation.
 *
 * C'est le seul moment ou un tiers - qui n'est pas notre client - decouvre
 * la qualite du produit. C'est donc notre meilleure publicite aupres de
 * futurs clients, et cela merite d'etre soigne.
 *
 * Decision du porteur du 16/09/2026 : apres sa signature, le signataire ne
 * doit JAMAIS attendre. Le document signe n'existe qu'une fois tous les
 * signataires passes, puis il est scelle en file (quelques secondes, parfois
 * davantage si l'horodatage tarde). Jusqu'au 16/09, l'ecran attendait 60 s
 * puis montrait des boutons qui ne telechargeaient rien. Desormais, le meme
 * message pour tous : la signature est enregistree, le document signe sera
 * envoye par e-mail (services/signed_document_notifications.py cote serveur).
 *
 * Un signataire qui rouvre son lien APRES la fin de l'enveloppe trouve les
 * boutons de telechargement - et un echec de telechargement est affiche,
 * jamais muet.
 *
 * Dossier de preuve (25/09/2026) : il se construit APRES la fin de
 * l'enveloppe - le temps de le sceller, plusieurs minutes si la file
 * d'horodatage est longue. Tant qu'il manque (proof_ready), l'ecran le dit
 * (« en cours de preparation ») au lieu d'offrir un bouton qui repondrait
 * 409 et afficherait une erreur de telechargement ; il reinterroge le
 * serveur toutes les 15 s pendant dix minutes, puis toutes les minutes
 * (relecture 3 : le message promet que le dossier « apparaitra ici », or le
 * scellement peut depasser dix minutes quand la file est longue), et montre
 * le bouton des que le dossier est la. Une relecture en echec (reseau coupe
 * un instant) garde l'ecran tel quel et reessaie : elle le faisait repasser
 * a « en attente des autres signataires ». Le document signe, lui, existe
 * des la fin.
 */
import { useEffect, useState } from "react";

import { compteExistant, creerCompte, fetchProofBundle, fetchSignedDocument, fetchView } from "../api";
import type { t } from "../i18n";

interface Props {
  token: string;
  L: ReturnType<typeof t>;
}

/** Relecture de la vue tant que le dossier de preuve se scelle. */
const INTERVALLE_PREUVE_MS = 15_000;
const RELECTURES_RAPIDES = 40; // dix minutes
const INTERVALLE_PREUVE_LENT_MS = 60_000;

export function Done({ token, L }: Props) {
  // null = pas encore su ; true = l'enveloppe est terminee, le document existe.
  const [documentPret, setDocumentPret] = useState<boolean | null>(null);
  // Le dossier de preuve, lui, arrive apres le document signe.
  const [preuvePrete, setPreuvePrete] = useState(false);
  const [titre, setTitre] = useState("document");
  const [downloadError, setDownloadError] = useState(false);
  const [compte, setCompte] = useState<"inconnu" | "existant" | "propose" | "envoi" | "cree" | "echec">("inconnu");
  const [mdp, setMdp] = useState("");
  useEffect(() => {
    void compteExistant(token).then((oui) => setCompte(oui ? "existant" : "propose"), () => setCompte("propose"));
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    let minuterie: number | undefined;
    let relectures = 0;
    let dejaLu = false;
    const relire = () => {
      relectures += 1;
      const delai = relectures <= RELECTURES_RAPIDES ? INTERVALLE_PREUVE_MS : INTERVALLE_PREUVE_LENT_MS;
      minuterie = window.setTimeout(() => void lire(), delai);
    };
    const lire = async () => {
      try {
        const view = await fetchView(token);
        if (cancelled) return;
        dejaLu = true;
        // Enveloppe terminee, pas seulement CE signataire signe (17/09/2026) :
        // avant la fin, le document signe et la preuve n'existent pas.
        const terminee = view.envelope_completed === true;
        const prete = view.proof_ready === true;
        setDocumentPret(terminee);
        setPreuvePrete(prete);
        if (view.title) setTitre(view.title);
        if (terminee && !prete) relire();
      } catch {
        if (cancelled) return;
        // Premiere lecture en echec : on ne sait rien, comme avant. Une
        // RELECTURE en echec ne change pas ce qu'on sait deja : on reessaie.
        if (dejaLu) relire();
        else setDocumentPret(false);
      }
    };
    void lire();
    return () => {
      cancelled = true;
      window.clearTimeout(minuterie);
    };
  }, [token]);

  const save = async (kind: "signed" | "proof") => {
    setDownloadError(false);
    let blob: Blob;
    try {
      blob = kind === "signed" ? await fetchSignedDocument(token) : await fetchProofBundle(token);
    } catch {
      setDownloadError(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Le dossier de preuve est un PDF scelle (FON-30), plus une archive zip :
    // le nom « .zip » le rendait impossible a ouvrir d'un double clic.
    a.download = kind === "signed" ? `${titre} - signé.pdf` : `${titre} - dossier de preuve.pdf`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <section className="card stack">
      <div className="fini" role="status">
        <svg className="fini__icone" viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
          <circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="3" />
          <path d="M14 25l7 7 13-15" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"
                strokeLinejoin="round" />
        </svg>
        <h2 className="fini__titre">{L.doneTitle}</h2>
      </div>

      {documentPret ? (
        <>
          <p>{L.doneBody}</p>
          <div className="actions">
            <button type="button" className="btn btn--primary" onClick={() => void save("signed")}>
              {L.downloadSigned}
            </button>
            {preuvePrete && (
              <button type="button" className="btn btn--secondary" onClick={() => void save("proof")}>
                {L.downloadProof}
              </button>
            )}
          </div>
          {!preuvePrete && (
            <div className="alert alert--info" role="status">
              {L.proofPreparing}
            </div>
          )}
          {downloadError && (
            <div className="alert alert--error" role="alert">
              {L.downloadFailed}
            </div>
          )}
        </>
      ) : documentPret === false ? (
        <p>{L.waitingOthers}</p>
      ) : null}

      {/* Porteur, 12/10/2026 : compte gratuit en UN geste - le mot de passe,
          l'adresse est connue ; rien a qui a deja un compte. */}
      {compte === "existant" ? (
        <div className="compte-gratuit">
          <h2 className="compte-gratuit__titre">{L.compteDejaTitre}</h2>
          <p style={{ margin: 0 }}>{L.compteDejaTexte}</p>
          <a className="btn btn--primary" href="https://app.sign365.io">{L.compteConnexion}</a>
        </div>
      ) : compte !== "inconnu" && (
        <form className="compte-gratuit" onSubmit={(e) => {
          e.preventDefault();
          if (mdp.length < 12) return;
          setCompte("envoi");
          void creerCompte(token, mdp).then(() => setCompte("cree"), () => setCompte("echec"));
        }}>
          <h2 className="compte-gratuit__titre">{L.compteTitre}</h2>
          <p style={{ margin: 0 }}>{L.compteTexte}</p>
          {compte === "cree" ? (
            <>
              <div className="alert alert--success" role="status">{L.compteCree}</div>
              <a className="btn btn--primary" href="https://app.sign365.io">{L.compteConnexion}</a>
            </>
          ) : (
            <>
              <input className="saisie compte-gratuit__mdp" type="password" autoComplete="new-password" minLength={12}
                     value={mdp} onChange={(e) => setMdp(e.target.value)} aria-label={L.compteMdp}
                     placeholder={L.compteMdp} />
              <button type="submit" className="btn btn--primary" disabled={compte === "envoi" || mdp.length < 12}>
                {L.compteBouton}
              </button>
              <p className="compte-gratuit__garanties">{L.compteGaranties}</p>
            </>
          )}
          {compte === "echec" && <div className="alert alert--error" role="alert">{L.compteEchec}</div>}
        </form>
      )}
    </section>
  );
}
